// Doctor/admin-only staff manager. Lets the clinic add reception/nurse
// emails so those people show up with the right role the first time they
// sign in with Google. Also flips existing users between roles.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, ShieldCheck, Users, X, Mail, Calendar as CalIcon, CheckCircle2, LinkIcon } from "lucide-react";
import { CLINIC, LogoTile } from "../lib/clinic";
import { useAuth } from "../lib/auth";

const ROLES = [
  { key: "doctor", label: "Doctor", tone: "bg-pink-100 text-pink-800 border-pink-200" },
  { key: "reception", label: "Reception", tone: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "nurse", label: "Nurse", tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "admin", label: "Admin", tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "suspended", label: "Suspended", tone: "bg-slate-200 text-slate-700 border-slate-300" },
];
const roleTone = (r) => ROLES.find((x) => x.key === r)?.tone || "bg-slate-100 text-slate-700 border-slate-200";

export default function StaffSettings() {
  const { refresh: refreshAuth } = useAuth() || {};
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("reception");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/staff");
      setUsers(r.data.users || []);
      setPending(r.data.pending_invites || []);
    } catch {
      toast.error("Failed to load staff");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // -------- Google Calendar connection --------
  const [calStatus, setCalStatus] = useState({ connected: false, google_email: null, connected_at: null });
  const [calBusy, setCalBusy] = useState(false);
  const loadCalStatus = async () => {
    try { const r = await api.get("/oauth/calendar/status"); setCalStatus(r.data || {}); } catch {}
  };
  useEffect(() => {
    loadCalStatus();
    // Toast the result of the OAuth round-trip when Google redirects back
    // to /settings/staff?calendar=connected|error.
    const params = new URLSearchParams(window.location.search);
    if (params.get("calendar") === "connected") {
      toast.success(`Google Calendar connected${params.get("as") ? ` as ${params.get("as")}` : ""}`);
      window.history.replaceState({}, "", "/settings/staff");
    } else if (params.get("calendar") === "error") {
      toast.error(`Calendar connect failed: ${params.get("msg") || "unknown"}`);
      window.history.replaceState({}, "", "/settings/staff");
    }
  }, []);
  const connectCalendar = async () => {
    setCalBusy(true);
    try {
      const r = await api.get("/oauth/calendar/connect");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start OAuth");
      setCalBusy(false);
    }
  };
  const disconnectCalendar = async () => {
    if (!window.confirm("Disconnect Google Calendar? Existing events will remain in your calendar; new follow-ups will not be synced.")) return;
    setCalBusy(true);
    try {
      await api.post("/oauth/calendar/disconnect");
      toast.success("Disconnected");
      loadCalStatus();
    } catch { toast.error("Disconnect failed"); }
    finally { setCalBusy(false); }
  };

  const invite = async () => {
    if (!inviteEmail.trim() || !inviteEmail.includes("@")) return toast.error("Valid email required");
    setInviting(true);
    try {
      const r = await api.post("/staff/invite", { email: inviteEmail.trim().toLowerCase(), role: inviteRole });
      toast.success(r.data.pending ? "Invite saved — they'll get this role on first login" : "Role updated");
      setInviteEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to invite");
    } finally { setInviting(false); }
  };

  const changeRole = async (email, role) => {
    try {
      await api.post("/staff/invite", { email, role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Suspend ${email}? They will lose access at their next login.`)) return;
    try {
      await api.delete(`/staff/${encodeURIComponent(email)}`);
      toast.success("Staff suspended");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // PHASE-B1: bind a user account to an authorised prescriber profile
  const linkDoctor = async (email, doctorId) => {
    try {
      await api.post("/staff/link-doctor", { email, doctor_id: doctorId });
      toast.success(doctorId ? "Prescriber linked" : "Prescriber unlinked");
      load();
      // MT-11B.7: the signed-in user's own linkage may have changed — refresh
      // the auth context from the server so the Rx page reflects it at once.
      refreshAuth?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="p-1.5 rounded hover:bg-slate-100" data-testid="staff-back"><ArrowLeft size={16} /></Link>
          <LogoTile />
          <div>
            <div className="text-sm font-heading font-semibold text-slate-900">Staff & Roles</div>
            <div className="text-[11px] text-slate-500">{CLINIC.name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-4">
        {/* --- Google Calendar integration (doctor's follow-up sync) --- */}
        <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="calendar-connect-card">
          <div className="flex items-center gap-2 mb-2">
            <CalIcon size={14} className="text-pink-600" />
            <div className="font-heading font-semibold text-sm">Google Calendar sync</div>
          </div>
          <div className="text-xs text-slate-600 mb-3 leading-relaxed">
            Connect your Google account so every visit saved with a follow-up interval
            (e.g. <em>15 days</em>, <em>1 month</em>) automatically creates a reminder event
            on your primary Google Calendar. Great for spotting clinic days at a glance.
          </div>
          {calStatus.connected ? (
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs">
                <CheckCircle2 size={12} /> Connected as <strong>{calStatus.google_email || "your Google account"}</strong>
              </div>
              <Button
                onClick={disconnectCalendar}
                disabled={calBusy}
                variant="outline"
                className="h-8 gap-1 text-xs border-rose-200 text-rose-700 hover:bg-rose-50"
                data-testid="calendar-disconnect-btn"
              >
                <X size={12} /> Disconnect
              </Button>
            </div>
          ) : (
            <Button
              onClick={connectCalendar}
              disabled={calBusy}
              className="bg-pink-600 hover:bg-pink-700 text-white gap-1.5 h-9"
              data-testid="calendar-connect-btn"
            >
              <LinkIcon size={13} /> {calBusy ? "Opening Google…" : "Connect Google Calendar"}
            </Button>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={14} className="text-pink-600" />
            <div className="font-heading font-semibold text-sm">Invite staff by email</div>
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Mail size={10} /> Email</label>
              <Input
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="reception@yourclinic.com"
                className="h-9 mt-0.5"
                data-testid="staff-invite-email"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="h-9 mt-0.5 border border-slate-300 rounded-md text-sm px-2"
                data-testid="staff-invite-role"
              >
                {ROLES.filter((r) => r.key !== "suspended").map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <Button onClick={invite} disabled={inviting} className="bg-pink-600 hover:bg-pink-700 text-white gap-1.5 h-9" data-testid="staff-invite-btn">
              <UserPlus size={13} /> {inviting ? "Sending…" : "Invite"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            They sign in with Google using this email — the role auto-applies. If they've already signed in, their role is updated immediately.
          </p>
        </section>

        <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2 font-heading font-semibold text-sm"><Users size={14} className="text-slate-500" /> Active users ({users.length})</div>
            <div className="text-[11px] text-slate-500">Pending invites: {pending.length}</div>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="p-3">Name / Email</th>
                <th className="p-3">Role</th>
                <th className="p-3">Prescriber</th>
                <th className="p-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={4} className="p-6 text-center text-slate-500">Loading…</td></tr>)}
              {!loading && users.map((u) => (
                <tr key={u.email} className="border-t border-slate-100" data-testid={`staff-row-${u.email}`}>
                  <td className="p-3">
                    <div className="font-medium text-slate-800">{u.name || u.email}</div>
                    <div className="text-[10px] text-slate-500 tabular">{u.email}</div>
                  </td>
                  <td className="p-3">
                    <select
                      value={u.role}
                      onChange={(e) => changeRole(u.email, e.target.value)}
                      className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded border ${roleTone(u.role)}`}
                      data-testid={`staff-role-${u.email}`}
                    >
                      {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    {/* PHASE-B1: prescriber linkage — required before this user can sign prescriptions */}
                    <select
                      value={u.doctor_id || ""}
                      onChange={(e) => linkDoctor(u.email, e.target.value)}
                      className="text-[11px] px-2 py-1 rounded border border-slate-300"
                      data-testid={`staff-doctor-link-${u.email}`}
                      title="Which doctor profile signs this user's prescriptions?"
                    >
                      <option value="">Not linked</option>
                      {(CLINIC.doctors || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => remove(u.email)}
                      className="text-red-600 hover:bg-red-50 p-1 rounded"
                      title="Suspend user"
                      data-testid={`staff-remove-${u.email}`}
                    >
                      <X size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && pending.map((p) => (
                <tr key={p.email} className="border-t border-slate-100 bg-amber-50/40" data-testid={`staff-pending-${p.email}`}>
                  <td className="p-3">
                    <div className="font-medium text-slate-800">{p.email}</div>
                    <div className="text-[10px] text-amber-700">Pending — has not signed in yet</div>
                  </td>
                  <td className="p-3">
                    <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded border ${roleTone(p.role)}`}>{p.role}</span>
                  </td>
                  <td className="p-3 text-[10px] text-slate-400">{p.expires_at ? `Invite expires ${String(p.expires_at).slice(0, 10)}` : "—"}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => remove(p.email)} className="text-red-600 hover:bg-red-50 p-1 rounded" title="Cancel invite"><X size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <div className="text-[11px] text-slate-500 bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2">
          <ShieldCheck size={14} className="text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <strong>Reception scope:</strong> can register patients and enter Name, Age, Gender, Referred by, Mobile, Weight, Height, Waist, BP, Pulse, SpO₂, and upload patient photo / reports. Everything clinical (diagnosis, prescription, guidance, calculators) is doctor-only — reception cannot even open the doctor's patient page.
          </div>
        </div>
      </main>
    </div>
  );
}
